import { TutorialSection, TutorialSectionResponse, SingleSectionResponse, SingleLessonResponse } from '@/types/tutorial';
import { getPublicApiBaseUrl } from '@/utils/publicApiBaseUrl';

/**
 * Tutorial Service - All data is fetched from the database via API.
 *
 * To manage playbook content (sections, lessons, categories), use the database directly.
 * See the backend migrations for the database schema:
 * - tutorial_sections: Categories/courses (title, slug, description, level, category, cover_image)
 * - tutorial_lessons: Individual lessons (title, slug, description, video_url, duration)
 */
export const tutorialService = {
    /**
     * Fetch all tutorial sections (playbook categories) from the database
     */
    async getSections(): Promise<TutorialSection[]> {
        try {
            const response = await fetch(`${getPublicApiBaseUrl()}/public/tutorials/sections`);
            if (!response.ok) throw new Error('Failed to fetch sections');
            const data: TutorialSectionResponse = await response.json();
            return data.sections;
        } catch (error) {
            console.error('Error fetching tutorial sections:', error);
            // Return empty array if API fails - data must come from database
            return [];
        }
    },

    /**
     * Fetch a single section with its lessons from the database
     */
    async getSectionById(sectionId: string): Promise<TutorialSection | null> {
        try {
            const response = await fetch(`${getPublicApiBaseUrl()}/public/tutorials/sections/${sectionId}`);
            if (!response.ok) throw new Error('Failed to fetch section');
            const data: SingleSectionResponse = await response.json();
            return data.section;
        } catch (error) {
            console.error('Error fetching section:', error);
            return null;
        }
    },

    /**
     * Fetch a single lesson from the database
     */
    async getLesson(sectionId: string, lessonId: string): Promise<SingleLessonResponse['lesson'] | null> {
        try {
            const response = await fetch(`${getPublicApiBaseUrl()}/public/tutorials/sections/${sectionId}/lessons/${lessonId}`);
            if (!response.ok) throw new Error('Failed to fetch lesson');
            const data: SingleLessonResponse = await response.json();
            return data.lesson;
        } catch (error) {
            console.error('Error fetching lesson:', error);
            return null;
        }
    }
};
